// src/utils/datetime.utils.ts
import dayjs from "../dayjs-setup.ts";

/**
 * Formatea una fecha UTC a la zona horaria local del navegador.
 * Si el string no termina en "Z", se le agrega para que dayjs lo interprete como UTC.
 * @param dateString Fecha en formato ISO (con o sin "Z" final)
 * @param format Formato de salida (por defecto "MMM DD, YYYY, hh:mm A")
 */
export function formatUTCToLocal(
  dateString: string,
  format: string = "MMM DD, YYYY, hh:mm A"
): string {
  if (!dateString) return "";
  const normalized = /[zZ]$/.test(dateString) ? dateString : `${dateString}Z`;
  return dayjs(normalized).format(format);
}

/** Normaliza a ISO-UTC (Z). Si el string no trae offset, lo interpreta en `tz`. */
export function toUTCISO(input: string | Date | number, tz?: string): string {
  const s = typeof input === "string" ? input.trim() : "";
  const hasTZOffset =
    typeof input === "string" && /Z|[+\-]\d{2}:\d{2}$/.test(s);

  if (!hasTZOffset && typeof input === "string") {
    return dayjs
      .tz(s, tz || dayjs.tz.guess())
      .utc()
      .toISOString();
  }
  return dayjs(input).utc().toISOString();
}

/**
 * Convierte un inputDate a UTC ISO string,
 * interpretándolo en el timezone que recibes (tz).
 */
export function normalizeToUTC(
  input: string | Date | number | dayjs.Dayjs,
  tz: string
): string {
  //const dateT = dayjs(input)?.format("YYYY-MM-DDTHH:mm:ss");
  const d = dayjs(input);
  // String "naive" con componentes locales
  const naive = d.format("YYYY-MM-DD HH:mm:ss.SSS");
  return dayjs.tz(naive, tz).utc().toISOString();
}


/**
 * Recibe una fecha en UTC y la presenta según el timezone y formato deseados.
 * @param input Fecha en UTC (string, Date o dayjs)
 * @param tz Timezone destino, ej: "America/Dawson"
 * @param format Formato de salida, ej: "YYYY-MM-DD HH:mm:ss"
 */
export function formatFromUTC(
  input: string | Date | number | dayjs.Dayjs,
  tz: string,
  format: string = "YYYY-MM-DD HH:mm:ss"
): string {
  return dayjs.utc(input).tz(tz).format(format);
}

export function showDateTextTZ (
  input: string | Date | number | dayjs.Dayjs,
  tz: string,
  format: string = "YYYY-MM-DD HH:mm:ss"
): string {

  return `${dayjs.utc(input).tz(tz).format(format)} ${tz ? `(${tz})` : ""}`;
}

/**
 * Convierte un instante en UTC (string/Date/number/dayjs con Z)
 * a un Date para el datepicker que muestre la misma "hora de pared"
 * en el timezone `tz`. (El Date resultante está en la zona local
 * del navegador, pero con los mismos componentes Y-M-D H:m:s del tz).
 */
export function utcToPickerDate(
  utcInput: string | Date | number | dayjs.Dayjs,
  tz: string
): Date {
  const z = dayjs.utc(utcInput).tz(tz); // -> hora de pared del tz
  // Construimos un Date local con esos componentes (evita problemas de parseo)
  return new Date(
    z.year(),
    z.month(),       // 0-11
    z.date(),
    z.hour(),
    z.minute(),
    z.second(),
    z.millisecond()
  );
}

/**
 * Toma el Date que entrega el picker (en zona local del navegador),
 * lo interpreta como hora de pared del timezone `tz`,
 * y lo convierte a UTC en ISO (para guardar).
 */
// export function pickerDateToUTC(
//   pickerDate: Date | string | number | dayjs.Dayjs,
//   tz: string
// ): string {
//   const d = dayjs(pickerDate);
//   // String "naive" con componentes locales
//   const naive = d.format("YYYY-MM-DD HH:mm:ss.SSS");
//   // Interprétalo como hora de pared del tz y manda a UTC
//   return dayjs.tz(naive, tz).utc().toISOString();
// }